import * as React from 'react';
import { CalendarIcon } from 'lucide-react';

import { controlBase, controlStates } from '../../lib/control';
import { dateKeyOf, longDateLabel, parseDateKey } from '../../lib/format';
import { cn } from '../../lib/utils';
import { Calendar } from '../shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';

/**
 * A date, picked off a calendar. The value is a `yyyy-mm-dd` key, the same
 * shape the API takes and a native date input holds, so a schema checks it as
 * plain text.
 *
 * Drawn in the same box as Input, so it sits in a row of fields without
 * reading as a different kind of thing. Takes what `Field` hands a control —
 * `id`, the aria pair, `value`, `onChange`, `onBlur` — so it binds the same
 * way: `<Field name="date" label="Date"><DatePicker /></Field>`.
 *
 * `min`, `max` and `closedWeekdays` grey out the days that cannot be picked —
 * the past, dates outside a campaign, or days a room is shut — so the visitor
 * never picks one only to be told no.
 */
function DatePicker({
  id,
  value = '',
  onChange,
  onBlur,
  min,
  max,
  closedWeekdays,
  placeholder = 'Pick a date',
  disabled,
  className,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value?: string;
  onChange?: (next: string) => void;
  onBlur?: () => void;
  /** The first day that can be picked, as a `yyyy-mm-dd` key. */
  min?: string;
  /** The last day that can be picked, as a `yyyy-mm-dd` key. */
  max?: string;
  /** Weekdays that cannot be picked: 0 is Sunday, 6 is Saturday. */
  closedWeekdays?: number[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseDateKey(value) : undefined;
  // The calendar greys out a day that matches any rule in this list.
  const unpickable = [
    ...(min ? [{ before: parseDateKey(min) }] : []),
    ...(max ? [{ after: parseDateKey(max) }] : []),
    ...(closedWeekdays?.length ? [{ dayOfWeek: closedWeekdays }] : []),
  ];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Left once the calendar closes, so a field checked on blur is
        // checked after the pick rather than while the visitor is choosing.
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          className={cn(
            controlBase,
            controlStates,
            'h-9 items-center justify-between gap-2 text-left',
            !value && 'text-ink-muted',
            className,
          )}
        >
          <span className="truncate">{value ? longDateLabel(value) : placeholder}</span>
          <CalendarIcon aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          disabled={unpickable}
          onSelect={(day) => {
            if (day) onChange?.(dateKeyOf(day));
            setOpen(false);
            onBlur?.();
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
